import { noSqlDb } from '../database/no-sql-db';
import { sharedExecute } from '../other/common/helpers/callbacks';
import { isEmptyOrSpace } from '../other/ui/string-helper-functions';

export interface IPersistentApi<TInput, TResult> {
  run: (input: TInput) => Promise<TResult | null>;
  runSync(input: TInput): TResult | null;
  flushCache: (input?: TInput) => Promise<void>;
}

interface PersistentApiState {
  keys: string[]; // persisted as array; internally we dedupe with a Set
  version: string;
}

interface PersistenceExecutionPlan<TResult> {
  execute: () => Promise<{ old: TResult | null; new: TResult | null }>;
  value?: TResult | null;
}

/** -------- Utilities: canonical stringify + short hash for stable, compact keys -------- */

function canonicalStringify(value: unknown): string {
  // Keeps arrays ordered; sorts object keys to make stringification stable across key orders.
  const seen = new WeakSet<object>();
  const stringify = (v: any): string => {
    if (v === undefined) return '"[Undefined]"';
    if (v === null || typeof v !== 'object') return JSON.stringify(v);
    if (seen.has(v)) return '"[Circular]"';
    seen.add(v);

    if (Array.isArray(v)) {
      const arr = v.map(x => JSON.parse(stringify(x)));
      return JSON.stringify(arr);
    }

    // plain object
    const keys = Object.keys(v).sort();
    const obj: Record<string, unknown> = {};
    for (const k of keys) {
      obj[k] = JSON.parse(stringify(v[k]));
    }
    return JSON.stringify(obj);
  };
  return stringify(value);
}

function djb2Hash(str: string): string {
  // Simple stable hash; fast and dependency-free. Not cryptographic.
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    // hash * 33 + char
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  // Convert to unsigned 32-bit and hex
  return (hash >>> 0).toString(16);
}

function buildParamId(input: unknown): string {
  const canonical = canonicalStringify(input);
  // Param ID format keeps a short hash + length to avoid pathological collisions,
  // but still enables basic debugging.
  return `${djb2Hash(canonical)}_${canonical.length}`;
}

function toKvKey(cacheKey: string, paramId: string): string {
  return `${cacheKey}:param:${paramId}`;
}

/** -------------------------------- Implementation -------------------------------- */

const DEBUG_LOG_ENABLED = false;
function debugLog(...args: any[]) {
  if (DEBUG_LOG_ENABLED) {
    console.debug(...args);
  }
}

class PersistentApi<TInput, TResult> implements IPersistentApi<TInput, TResult> {
  state: PersistentApiState = {
    keys: [],
    version: ''
  };

  /** In-memory execution plans keyed by paramId (NOT the full kv key). */
  private executers: Map<string, PersistenceExecutionPlan<TResult>> = new Map();

  /** We use a Set for de-duplication internally; persisted as array. */
  private get stateKeySet(): Set<string> {
    return new Set(this.state.keys);
  }
  private setStateKeysFromSet(s: Set<string>) {
    this.state.keys = Array.from(s);
  }

  private initialized = false;

  constructor(
    private options: {
      getVersion?: () => Promise<string>;
      apiFunction: (input: TInput) => Promise<TResult | null>;
      flushEventTrigger?: (flushEvent: (input?: TInput) => Promise<void>) => Promise<void>;
      afterUpdateEvent?: (oldValue: TResult | null, newValue: TResult | null) => Promise<void>;
      cacheKey: string;
      expiryTime?: number; // seconds; default 7 days
      overwriteOnVersionMismatch?: boolean;
      allowNullResults?: boolean;
    }
  ) {}

  /** Build a fresh state with current version. */
  async blankState(): Promise<PersistentApiState> {
    return {
      keys: [],
      version: this.options.getVersion ? await this.options.getVersion() : ''
    };
  }

  /** Persist current state (optionally clearing/re-initializing it first). */
  async saveState(clearState = false) {
    if (clearState) {
      this.state = await this.blankState();
    }
    await noSqlDb.grab(
      `${this.options.cacheKey}:state`,
      async () => this.state,
      this.options.expiryTime ?? 604800, // default 7 days
      undefined,
      true
    );
  }

  /** Load or initialize the persisted state. */
  async loadState() {
    const blankState = await this.blankState();
    this.state =
      (await noSqlDb.grab<PersistentApiState>(
        `${this.options.cacheKey}:state`,
        async () => blankState,
        this.options.expiryTime ?? 604800
      )) || blankState;
  }

  /** One-time initializer guarded by sharedExecute to dedupe concurrent callers. */
  private init = sharedExecute(async () => {
    if (this.initialized) return;
    const startTime = performance.now();
    try {
      await this.loadState();
      if (this.options.flushEventTrigger) {
        await this.options.flushEventTrigger(this.flushCache);
      }
    } finally {
      this.initialized = true;
      const duration = performance.now() - startTime;
      debugLog(`PersistentApi init [${this.options.cacheKey}]: ${duration.toFixed(2)}ms`);
    }
  });

  /**
   * Core flush logic:
   * - If `input` provided: unconditionally flush that input's KV + in-memory value & remove from state.
   * - Else: full flush when called, resetting state + in-memory executers.
   */
  private async internalFlushCache(input?: TInput) {
    const startTime = performance.now();
    const isTargeted = input !== undefined;

    try {
      if (isTargeted) {
        const paramId = buildParamId(input);
        const kvKey = toKvKey(this.options.cacheKey, paramId);

        await noSqlDb.delGrabItem(kvKey);

        const plan = this.executers.get(paramId);
        if (plan) plan.value = undefined;

        const set = this.stateKeySet;
        if (set.delete(kvKey)) {
          this.setStateKeysFromSet(set);
          await this.saveState();
        }
        return;
      }

      // Full flush: drop state record and all param entries
      await noSqlDb.delGrabItem(`${this.options.cacheKey}:state`);
      for (const kvKey of this.state.keys) {
        await noSqlDb.delGrabItem(kvKey);
      }

      // Clear in-memory cache values
      this.executers.forEach(plan => {
        plan.value = undefined;
      });

      // Reset state with fresh version
      this.state = await this.blankState();
      await this.saveState();
    } catch (error) {
      console.error('internalFlushCache failed:', error);
    } finally {
      const duration = performance.now() - startTime;
      debugLog(
        `PersistentApi flush [${this.options.cacheKey}]: ${duration.toFixed(2)}ms` +
          (isTargeted ? ` (targeted: ${buildParamId(input)})` : ' (full)')
      );
    }
  }

  /**
   * Public flush API:
   * - If `input` is provided, flush that input unconditionally (expected behavior).
   * - If no input, perform a version-guarded full flush: only when version changed or is empty.
   */
  public flushCache = async (input?: TInput) => {
    if (input !== undefined) {
      await this.internalFlushCache(input);
      return;
    }

    const version = this.state.version;
    const currentVersion = this.options.getVersion ? await this.options.getVersion() : '';
    if (version !== currentVersion || isEmptyOrSpace(version)) {
      await this.internalFlushCache(); // full flush
    }
  };

  /**
   * Run the API call with caching/locking:
   * - Ensures initialized.
   * - Builds/looks up an executer per paramId.
   * - Optionally triggers a full flush if overwriteOnVersionMismatch is set and version changed.
   * - Uses grabOldAndNew to atomically compute/update the value.
   */
  async run(input: TInput): Promise<TResult | null> {
    const startTime = performance.now();
    let cacheHit = false;
    let executionTime = 0;

    if (!this.initialized) await this.init();

    const paramId = buildParamId(input);
    let executer = this.executers.get(paramId);
    if (!executer) {
      const execute = this.createExecuter(paramId, input);
      executer = { execute, value: undefined };
      this.executers.set(paramId, executer);
    }

    // Optional global reset on version change (kept for feature parity).
    if (this.options.overwriteOnVersionMismatch) {
      const currentVersion = this.options.getVersion ? await this.options.getVersion() : '';
      if (this.state.version !== currentVersion) {
        await this.internalFlushCache(); // full reset
      }
    }

    if (executer.value !== undefined) {
      cacheHit = true;
      const endTime = performance.now();
      debugLog(`PersistentApi cache hit [${this.options.cacheKey}:${paramId}]: ${endTime - startTime}ms`);
      return executer.value;
    }

    try {
      const executeStartTime = performance.now();
      const values = await executer.execute();
      executionTime = performance.now() - executeStartTime;

      executer.value = this.options.allowNullResults ? values.new : (values.new ?? undefined);

      if (this.options.afterUpdateEvent) {
        try {
          await this.options.afterUpdateEvent(values.old, values.new);
        } catch (evtErr) {
          console.error('afterUpdateEvent failed:', evtErr);
          // Do not unset the cached value if the side-effect hook fails.
        }
      }
    } catch (error) {
      executer.value = undefined;
      console.error('Error executing PersistentApi:', error);
      throw error;
    } finally {
      const endTime = performance.now();
      const totalTime = endTime - startTime;

      debugLog(
        `PersistentApi ${cacheHit ? 'hit' : 'miss'} [${this.options.cacheKey}:${paramId}]: ${totalTime.toFixed(2)}ms` +
          (cacheHit ? '' : ` (API execution: ${executionTime.toFixed(2)}ms)`)
      );
    }

    return executer.value ?? null;
  }

  /**
   * Synchronous read of the last computed value for a given input.
   * Requires that run() has been called for that input previously.
   */
  runSync(input: TInput): TResult | null {
    if (!this.initialized) throw new Error('PersistentApi not initialized. Call run() first.');
    const paramId = buildParamId(input);
    const executer = this.executers.get(paramId);
    if (!executer) {
      throw new Error(`PersistentApi not initialized for input: ${paramId}`);
    }
    return executer.value ?? null;
  }

  /**
   * Creates a single-flight executer for a specific paramId.
   * Registers the KV key in state (de-duplicated) and relies on grabOldAndNew
   * to handle atomic compute/update and return { old, new }.
   */
  private createExecuter(paramId: string, input: TInput): () => Promise<{ old: TResult | null; new: TResult | null }> {
    return sharedExecute(async () => {
      const startTime = performance.now();
      const kvKey = toKvKey(this.options.cacheKey, paramId);

      // Record the kvKey in the state (deduped)
      const set = this.stateKeySet;
      if (!set.has(kvKey)) {
        set.add(kvKey);
        this.setStateKeysFromSet(set);
        await this.saveState();
      }

      const overwriteOnVersionMismatch =
        !!this.options.overwriteOnVersionMismatch && this.state.version !== ((await this.options.getVersion?.()) ?? '');

      let dbStartTime = performance.now();
      try {
        return await noSqlDb.grabOldAndNew<TResult>(
          kvKey,
          async () => {
            const apiFnStartTime = performance.now();
            try {
              return await this.options.apiFunction(input);
            } finally {
              const apiFnTime = performance.now() - apiFnStartTime;
              debugLog(`PersistentApi apiFunction [${this.options.cacheKey}:${paramId}]: ${apiFnTime.toFixed(2)}ms`);
            }
          },
          this.options.expiryTime ?? 604800,
          undefined,
          overwriteOnVersionMismatch
        );
      } finally {
        const dbTime = performance.now() - dbStartTime;
        const totalTime = performance.now() - startTime;
        debugLog(
          `PersistentApi executer [${this.options.cacheKey}:${paramId}]: total=${totalTime.toFixed(2)}ms, db=${dbTime.toFixed(2)}ms`
        );
      }
    });
  }
}

/** Builder */
export function buildPersistentApi<TInput, TResult>(options: {
  apiFunction: (input: TInput) => Promise<TResult | null>;
  flushEventTrigger?: (flushEvent: (input?: TInput) => Promise<void>) => Promise<void>;
  getVersion?: () => Promise<string>;
  afterUpdateEvent?: (oldValue: TResult | null, newValue: TResult | null) => Promise<void>;
  cacheKey: string;
  expiryTime?: number;
  overwriteOnVersionMismatch?: boolean;
  allowNullResults?: boolean;
}): IPersistentApi<TInput, TResult> {
  const api = new PersistentApi<TInput, TResult>(options);
  return api;
}
